import { flushRevocations, queueRevocation } from '../lib/privacy/revocations';
import { apiRequest } from '../lib/apiClient';
import { authService } from '../lib/authService';
/**
 * Consent Context Provider
 *
 * Provides consent state and management to the application.
 * Integrates with TenantContext for policy configuration.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import type {
  ConsentScope,
  UserConsentState,
  ConsentCheckResult,
  ConsentPolicy,
  ConsentEvent,
  AgeGroup,
  ParentalConsentStatus,
} from '../lib/consent/types';
import { DATA_CLASSIFICATION } from '../lib/consent/types';
import { ConsentService } from '../lib/consent/service';
import { useTenant } from './TenantContext';

// ============================================
// CONTEXT TYPES
// ============================================

interface ConsentContextValue {
  // State
  isInitialized: boolean;
  isLoading: boolean;
  state: UserConsentState | null;
  ageGroup: AgeGroup | null;
  parentalConsentStatus: ParentalConsentStatus;

  // Age verification
  needsAgeVerification: boolean;
  needsParentalConsent: boolean;
  setAge: (birthYear: number) => Promise<void>;
  setAgeBand: (ageGroup: AgeGroup) => Promise<void>;

  // Parental consent flow
  requestParentalConsent: (parentEmail: string) => Promise<string>;
  verifyParentalConsent: (code: string) => Promise<boolean>;

  // Consent management
  grantConsent: (scope: ConsentScope) => Promise<ConsentCheckResult>;
  revokeConsent: (scope: ConsentScope) => Promise<void>;
  revokeAllConsent: () => Promise<void>;

  // Consent checks
  hasConsent: (scope: ConsentScope) => boolean;
  canProcessData: (dataType: keyof typeof DATA_CLASSIFICATION) => boolean;
  getGrantedScopes: () => ConsentScope[];

  // Policy
  policy: ConsentPolicy | null;
}

// ============================================
// CONTEXT
// ============================================

const ConsentContext = createContext<ConsentContextValue | null>(null);

// ============================================
// PROVIDER
// ============================================

interface ConsentProviderProps {
  children: ReactNode;
  /**
   * User ID for consent association
   * If not provided, generates a local anonymous ID
   */
  userId?: string;
}

export function ConsentProvider({
  children,
  userId,
}: ConsentProviderProps): JSX.Element {
  const { tenant } = useTenant();
  const [service, setService] = useState<ConsentService | null>(null);
  const [state, setState] = useState<UserConsentState | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const effectiveUserId = userId || 'public';

  // Initialize consent service when tenant changes
  useEffect(() => {
    let active = true;
    let unsubscribe: (() => void) | undefined;
    const initService = async () => {
      setIsLoading(true);

      try {
        // Get policy from tenant config
        const policy = tenant.policy?.consent
          ? {
              minAgeNoParentalConsent: tenant.policy.age?.coppaAge ?? 13,
              defaultScopes: {
                under_13: ['personal_only'] as ConsentScope[],
                '13_to_17': ['personal_only'] as ConsentScope[],
                '18_plus': ['personal_only'] as ConsentScope[],
              },
              requiredScopes: [] as ConsentScope[],
              disabledScopes: [] as ConsentScope[],
              consentExpirationDays: 365,
              allowFullRevocation: true,
              parentalVerificationMethod: 'email' as const,
            }
          : undefined;

        const svc = new ConsentService(tenant.id, policy);
        const initialState = await svc.initialize(effectiveUserId);

        // Subscribe to consent events
        if (!active) return;
        unsubscribe = svc.onConsentEvent((_event: ConsentEvent) => {
          if (!active) return;
          // Refresh state on any consent change
          setState(svc.getState());
        });

        setService(svc);
        setState(initialState);
        setIsInitialized(true);
      } catch (err) {
        console.error('[ConsentProvider] Initialization error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    void initService();
    return () => { active = false; unsubscribe?.(); };
  }, [tenant.id, effectiveUserId]);

  // Age verification
  const needsAgeVerification = useMemo(() => {
    if (!state) return true;
    return state.ageVerification.method === 'self_reported' &&
           !state.ageVerification.verifiedAt;
  }, [state]);

  const needsParentalConsent = useMemo(() => {
    if (!service) return false;
    return service.needsParentalConsent();
  }, [service, state]); // Re-check when state changes

  const setAge = useCallback(async (birthYear: number): Promise<void> => {
    if (!service) throw new Error('Consent service not initialized');
    await service.setAgeGroup(birthYear);
    setState(service.getState());
  }, [service]);

  const setAgeBand = useCallback(async (ageGroup: AgeGroup) => {
    if (!service) throw new Error('Consent service not initialized');
    // Local-only guests need no network. Signed-in cloud requests require a server record.
    if (authService.getStatus() === 'authenticated') {
      await apiRequest('/privacy/age', { method: 'POST', body: JSON.stringify({ ageGroup }) });
    }
    await service.setAgeBand(ageGroup);
    setState(service.getState());
  }, [service]);

  useEffect(() => { const flush=()=>{ if(authService.getStatus()==='authenticated') void flushRevocations().catch(()=>{}); }; window.addEventListener('online',flush); flush(); return ()=>window.removeEventListener('online',flush); }, []);

  // Parental consent flow
  const requestParentalConsent = useCallback(async (parentEmail: string): Promise<string> => {
    if (!service) throw new Error('Consent service not initialized');
    const code = await service.requestParentalConsent(parentEmail);
    setState(service.getState());
    return code;
  }, [service]);

  const verifyParentalConsent = useCallback(async (code: string): Promise<boolean> => {
    if (!service) throw new Error('Consent service not initialized');
    const result = await service.verifyParentalConsent(code);
    setState(service.getState());
    return result;
  }, [service]);

  // Consent management
  const grantConsent = useCallback(async (scope: ConsentScope): Promise<ConsentCheckResult> => {
    if (!service) throw new Error('Consent service not initialized');
    if (scope !== 'personal_only') { await flushRevocations(); await apiRequest('/privacy/consent', { method: 'POST', body: JSON.stringify({ scope, granted: true }) }); }
    const result = await service.grantConsent(scope);
    setState(service.getState());
    return result;
  }, [service]);

  const revokeConsent = useCallback(async (scope: ConsentScope): Promise<void> => {
    if (!service) throw new Error('Consent service not initialized');
    await service.revokeConsent(scope);
    setState(service.getState());
    if (scope !== 'personal_only') await queueRevocation(scope);
  }, [service]);

  const revokeAllConsent = useCallback(async (): Promise<void> => {
    if (!service) throw new Error('Consent service not initialized');
    await service.revokeAllConsent();
    setState(service.getState());
    for(const scope of ['share_with_parent','share_with_counselor','institution_aggregate_only']) { try { await queueRevocation(scope); } catch { /* Durable retry on reconnect. */ } }
  }, [service]);

  // Consent checks
  const hasConsent = useCallback((scope: ConsentScope): boolean => {
    if (!service) return false;
    return service.hasConsent(scope).allowed;
  }, [service]);

  const canProcessData = useCallback((dataType: keyof typeof DATA_CLASSIFICATION): boolean => {
    if (!service) return false;
    return service.canProcessData(dataType).allowed;
  }, [service]);

  const getGrantedScopes = useCallback((): ConsentScope[] => {
    if (!service) return [];
    return service.getGrantedScopes();
  }, [service]);

  // Context value
  const value = useMemo<ConsentContextValue>(() => ({
    isInitialized,
    isLoading,
    state,
    ageGroup: state?.ageVerification.verifiedAt ? service?.getAgeGroup() ?? null : null,
    parentalConsentStatus: service?.getParentalConsentStatus() ?? 'not_required',

    needsAgeVerification,
    needsParentalConsent,
    setAge,
    setAgeBand,

    requestParentalConsent,
    verifyParentalConsent,

    grantConsent,
    revokeConsent,
    revokeAllConsent,

    hasConsent,
    canProcessData,
    getGrantedScopes,

    policy: service?.getPolicy() ?? null,
  }), [
    isInitialized,
    isLoading,
    state,
    service,
    needsAgeVerification,
    needsParentalConsent,
    setAge,
    setAgeBand,
    requestParentalConsent,
    verifyParentalConsent,
    grantConsent,
    revokeConsent,
    revokeAllConsent,
    hasConsent,
    canProcessData,
    getGrantedScopes,
  ]);

  return (
    <ConsentContext.Provider value={value}>
      {children}
    </ConsentContext.Provider>
  );
}

// ============================================
// HOOKS
// ============================================

/**
 * Access the consent context
 */
export function useConsent(): ConsentContextValue {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error('useConsent must be used within ConsentProvider');
  }
  return context;
}

/**
 * Check consent for a specific scope
 */
export function useConsentScope(scope: ConsentScope): {
  hasConsent: boolean;
  isLoading: boolean;
  grant: () => Promise<ConsentCheckResult>;
  revoke: () => Promise<void>;
} {
  const {
    hasConsent,
    grantConsent,
    revokeConsent,
    isLoading,
  } = useConsent();

  return {
    hasConsent: hasConsent(scope),
    isLoading,
    grant: () => grantConsent(scope),
    revoke: () => revokeConsent(scope),
  };
}

/**
 * Check if data processing is allowed
 */
export function useDataConsent(dataType: keyof typeof DATA_CLASSIFICATION): boolean {
  const { canProcessData, isInitialized } = useConsent();

  if (!isInitialized) return false;
  return canProcessData(dataType);
}

/**
 * Age-gate hook - returns null if age verification needed
 */
export function useAgeGate<T>(content: T): T | null {
  const { needsAgeVerification, needsParentalConsent, isLoading } = useConsent();

  if (isLoading) return null;
  if (needsAgeVerification || needsParentalConsent) return null;
  return content;
}

// ============================================
// AGE GATE COMPONENT
// ============================================

interface AgeGateProps {
  children: ReactNode;
  fallback?: ReactNode;
  onNeedsVerification?: () => void;
}

/**
 * Component that gates content behind age verification
 */
export function AgeGate({
  children,
  fallback = null,
  onNeedsVerification,
}: AgeGateProps): JSX.Element {
  const { needsAgeVerification, needsParentalConsent, isLoading } = useConsent();

  useEffect(() => {
    if (!isLoading && (needsAgeVerification || needsParentalConsent)) {
      onNeedsVerification?.();
    }
  }, [isLoading, needsAgeVerification, needsParentalConsent, onNeedsVerification]);

  if (isLoading) {
    return <>{fallback}</>;
  }

  if (needsAgeVerification || needsParentalConsent) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

// ============================================
// CONSENT GATE COMPONENT
// ============================================

interface ConsentGateProps {
  scope: ConsentScope;
  children: ReactNode;
  fallback?: ReactNode;
  onNeedsConsent?: (scope: ConsentScope) => void;
}

/**
 * Component that gates content behind consent for a specific scope
 */
export function ConsentGate({
  scope,
  children,
  fallback = null,
  onNeedsConsent,
}: ConsentGateProps): JSX.Element {
  const { hasConsent, isLoading } = useConsent();
  const allowed = hasConsent(scope);

  useEffect(() => {
    if (!isLoading && !allowed) {
      onNeedsConsent?.(scope);
    }
  }, [isLoading, allowed, scope, onNeedsConsent]);

  if (isLoading) {
    return <>{fallback}</>;
  }

  if (!allowed) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

